<template>
  <div
    class="overview-root"
    :class="{ 'is-mobile': isMobile }"
  >
    <!-- スマホ幅はカラムをタブ切替にする -->
    <v-tabs
      v-if="isMobile"
      v-model="mobileTab"
      grow
      height="42"
      class="overview-mobile-tabs"
    >
      <v-tab>お客様</v-tab>
      <v-tab>セッション</v-tab>
      <v-tab>次の延長</v-tab>
    </v-tabs>

    <div class="overview-columns">
      <div
        v-show="!isMobile || mobileTab === 0"
        class="overview-col"
      >
        <customer-column
          :table-id="tableId"
          :table-number="tableNumber"
          :table-prefix="tablePrefix"
          :table-suffix="tableSuffix"
          :custom-name="customName"
          :start-at="startAt"
          :customer-count="customerCount"
          :total="total"
          :customer-table-taggings="customerTableTaggings"
          :customer-ids="customerIds"
          :courses="courses"
          :self-order-status="selfOrderStatus"
          :hall-name="hallName"
          :deposits="deposits"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
          @updated:referring-histories="$emit('updated:referring-histories', $event)"
        />
      </div>

      <div
        v-show="!isMobile || mobileTab === 1"
        class="overview-col"
      >
        <session-column
          :table-id="tableId"
          :today="today"
          :start-at="startAt"
          :expire-at="expireAt"
          :auto-extension-enabled="autoExtensionEnabled"
          :auto-extension-course-name="autoExtensionCourseName"
          :subtotal="subtotal"
          :total="total"
          :current-nominations="currentNominations"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
        />
      </div>

      <div
        v-show="!isMobile || mobileTab === 2"
        class="overview-col"
      >
        <extension-column
          :table-id="tableId"
          :table-number="tableNumber"
          :table-prefix="tablePrefix"
          :table-suffix="tableSuffix"
          :custom-name="customName"
          :start-at="startAt"
          :expire-at="expireAt"
          :customer-count="customerCount"
          :total="total"
          :paid-in-advance-amount="paidInAdvanceAmount"
          :total-deposit-amount="totalDepositAmount"
          :hall-name="hallName"
          @updated:table="$emit('updated:table', $event)"
          @updated:vending-histories="$emit('updated:vending-histories', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance } from '@vue/composition-api'
import CustomerColumn from './CustomerColumn.vue'
import SessionColumn from './SessionColumn.vue'
import ExtensionColumn from './ExtensionColumn.vue'

export default {
  components: {
    CustomerColumn,
    SessionColumn,
    ExtensionColumn,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    today: {
      type: String,
      required: true,
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    subtotal: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    currentNominations: {
      // NOTE: 中身は今生きてるNominationのvending_history
      type: Array,
      default: () => [],
    },
    courseOverview: {
      // NOTE: 中身はその卓で一番終わるまでの時間が長いコースのvendingHistory
      // 互換性のため残している（新レイアウトではvendingHistoriesから導出する）
      type: Object,
      default: () => {},
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    customerIds: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    selfOrderStatus: {
      type: String,
      default: null,
    },
    hallName: {
      type: String,
      default: '',
    },
    deposits: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy

    const mobileTab = ref(1) // NOTE: スマホでは最初にセッションを見せる

    const isMobile = computed(() => vm.$vuetify.breakpoint.xsOnly)

    const totalDepositAmount = computed(() => {
      return props.deposits.reduce((sum, deposit) => sum + (deposit.amount || 0), 0)
    })

    return {
      mobileTab,
      isMobile,
      totalDepositAmount,
    }
  },
}
</script>

<style lang="scss" scoped>
.overview-root {
  // NOTE: v8プロトタイプのダークトーン（color-pack無効のためCSS変数で配色）
  --overview-bg: #0b0b10;
  --overview-surface-1: #111116;
  --overview-surface-2: #18181f;
  --overview-surface-3: #212130;
  --overview-border: rgba(255, 255, 255, 0.07);
  --overview-text-1: #f2f2f7;
  --overview-text-3: #6a6a80;
  --overview-accent: #8b5cf6;
  --overview-accent-soft: rgba(139, 92, 246, 0.25);
  --overview-accent-softer: rgba(139, 92, 246, 0.14);
  --overview-emerald: #34d399;
  --overview-emerald-soft: rgba(52, 211, 153, 0.14);
  --overview-rose: #fb7185;

  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--overview-bg);
  color: var(--overview-text-1);
  border-radius: 12px;
}

.overview-columns {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr) 380px;
  gap: 0;
}

.overview-col {
  position: relative;
  min-width: 0;
  height: 100%;
  overflow-y: auto;
  padding: 16px 16px 4px;
  border-right: 1px solid var(--overview-border);

  &:last-child {
    border-right: none;
  }

  // NOTE: スクロール可能であることのヒント（下端フェード）
  &::after {
    content: '';
    display: block;
    position: sticky;
    bottom: -4px;
    height: 28px;
    margin: 0 -16px;
    background: linear-gradient(to bottom, rgba(11, 11, 16, 0), var(--overview-bg));
    pointer-events: none;
  }
}

// スマホ幅: 1カラム（タブ切替）
.is-mobile .overview-columns {
  grid-template-columns: 1fr;
}

.is-mobile .overview-col {
  border-right: none;
}

.overview-mobile-tabs {
  flex: 0 0 auto;
}

// NOTE: カラム内の各セクション見出し（子コンポーネントで共用）
::v-deep .overview-sec {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--overview-text-3);
  margin-bottom: 10px;
}

// NOTE: ダーク地の上でVuetifyのカードが浮かないように透明化
::v-deep .v-card {
  background-color: transparent !important;
}
</style>
